/**
 * Data layer for `advertiser-manager`. Thin typed fetch wrappers over /api/v1/advertiser-manager.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type AdvertiserStatus = "active" | "paused" | "pending" | "archived";

export interface AdvertiserManagerRow {
	id: string;
	tenant_id: string;
	name: string;
	contact_email: string;
	vertical: string;
	payment_terms: string;
	campaigns_count: number;
	pending_approvals: number;
	status: AdvertiserStatus;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListAdvertiserManagerResponse {
	count: number;
	items: AdvertiserManagerRow[];
}

export interface CreateAdvertiserManagerBody {
	name: string;
	contact_email: string;
	vertical: string;
	payment_terms: string;
	status?: AdvertiserStatus;
}

const BASE = "/api/v1/advertiser-manager";
const TENANT_ID = "t1";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAdvertiserManagerResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: {
			accept: "application/json",
			"x-tenant-id": TENANT_ID,
		},
	});
	return parseOrThrow<ListAdvertiserManagerResponse>(res);
}

export async function create(
	body: CreateAdvertiserManagerBody,
): Promise<AdvertiserManagerRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
			"x-tenant-id": TENANT_ID,
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AdvertiserManagerRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: {
			accept: "application/json",
			"x-tenant-id": TENANT_ID,
		},
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const advertiserManagerQueryKeys = {
	all: ["advertiser-manager"] as const,
	list: () => [...advertiserManagerQueryKeys.all] as const,
	detail: (id: string) => [...advertiserManagerQueryKeys.all, id] as const,
};
